import { SelectionChangeResponse, Tree, TreeGroup, TreeItem } from "@rio-cloud/rio-uikit";
import { useEffect, useMemo, useRef, useState } from "react";
import { Tag } from "../useAssetTree";
import { useT } from "@hooks";

interface IVehicleTree {
  setSelectedVehicles: (items: string[]) => void;
  setIncludeNullVehicle: (value: boolean) => void;
  includeNullVehicle: boolean;
  selectedVehicles: string[];
  tags: Tag[];
  assets: any;
}

const EMPTY_GROUP_ID = "empty";
const SEM_GRUPO_GROUP_ID = "sem-grupo";
const DESAGRUPADO_ITEM_ID = "__desagrupado_vehicle__";

const VehicleTree = ({
  setSelectedVehicles,
  setIncludeNullVehicle,
  includeNullVehicle,
  selectedVehicles,
  tags,
  assets,
}: IVehicleTree) => {
  const t = useT();
  const getAssetGroupIds = (res: any): string[] => {
    const groupItems = res?._embedded?.tags?.items ?? [];
    return groupItems.length ? groupItems.map((g: any) => g.id as string) : [EMPTY_GROUP_ID];
  };

  // Um grupo só é considerado selecionado quando TODOS os seus veículos estão selecionados.
  const buildInitialGroups = (selectedVehicleIds: string[]): string[] => {
    const selectedSet = new Set(selectedVehicleIds);
    const groupMembers: Record<string, string[]> = {};
    assets.forEach((res: any) => {
      getAssetGroupIds(res).forEach((gid: string) => {
        if (gid === EMPTY_GROUP_ID) return;
        if (!groupMembers[gid]) groupMembers[gid] = [];
        groupMembers[gid].push(res.id as string);
      });
    });
    const fullySelected = Object.keys(groupMembers).filter((gid) =>
      groupMembers[gid].every((id) => selectedSet.has(id))
    );
    return includeNullVehicle ? [...fullySelected, EMPTY_GROUP_ID, SEM_GRUPO_GROUP_ID] : fullySelected;
  };

  // Na remontagem (troca de categoria), com os assets já carregados, a seleção inicial reflete
  // o que está salvo na store (selectedVehicles + includeNullVehicle), em vez de re-selecionar
  // todos os veículos. Na primeira carga (assets ainda vazios) o efeito de auto-seleção abaixo
  // seleciona tudo.
  const [selectedAssets, setSelectedAssets] = useState<string[]>(() =>
    assets?.length ? [...(selectedVehicles ?? []), ...(includeNullVehicle ? [DESAGRUPADO_ITEM_ID] : [])] : []
  );
  const [selectedGroups, setSelectedGroups] = useState<string[]>(() =>
    assets?.length ? buildInitialGroups(selectedVehicles ?? []) : []
  );
  const [expandedGroups, setExpandedGroups] = useState<string[]>(() =>
    tags?.length ? [...tags.map((t: Tag) => t.id), EMPTY_GROUP_ID, SEM_GRUPO_GROUP_ID] : []
  );

  const convertGroupId = (items: Array<{ id: string }>) => {
    if (!items.length) {
      return [EMPTY_GROUP_ID];
    }

    return items.map((res) => res.id);
  };

  const items: TreeItem[] = useMemo(() => {
    const vehicleItems: TreeItem[] = assets.map((res: any) => {
      const groupIds = convertGroupId(res._embedded.tags.items);
      return {
        id: res.id,
        name: res.name,
        type: res.type,
        groupIds: groupIds,
      };
    });
    const desagrupadoItem: TreeItem = {
      id: DESAGRUPADO_ITEM_ID,
      name: t("assetTree.vehicle.noItem"),
      type: "filter",
      groupIds: [SEM_GRUPO_GROUP_ID],
    };
    return [...vehicleItems, desagrupadoItem];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, t]);

  const empty: TreeGroup = {
    id: EMPTY_GROUP_ID,
    name: t("assetTree.vehicle.noGroup"),
  };

  const semGrupo: TreeGroup = {
    id: SEM_GRUPO_GROUP_ID,
    name: t("assetTree.ungrouped"),
    position: "last",
  };

  const newTags = [...tags, empty, semGrupo];

  // Seleciona todos os veículos e grupos por padrão na primeira carga
  const initialLoadDone = useRef(assets?.length > 0);

  useEffect(() => {
    if (initialLoadDone.current || items.length <= 1) return;

    const allAssetIds = items.map((item) => item.id);
    const allGroupIds = newTags.map((tag) => tag.id);

    setSelectedAssets(allAssetIds);
    setSelectedGroups(allGroupIds);
    setExpandedGroups(allGroupIds);
    initialLoadDone.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const onSelectionChange = ({ groups, items: changedItems }: SelectionChangeResponse) => {
    setSelectedAssets(changedItems);
    setSelectedGroups(groups);
  };

  useEffect(() => {
    if (!initialLoadDone.current) return;

    const safeSelectedGroups = selectedGroups ?? [];
    const safeSelectedAssets = selectedAssets ?? [];
    const filteredItems = items.filter(
      (item) =>
        item.id !== DESAGRUPADO_ITEM_ID && item?.groupIds?.some((groupId) => safeSelectedGroups.includes(groupId))
    );
    const filteredIds = filteredItems.map((item) => item.id);
    const realSelectedAssets = safeSelectedAssets.filter((id) => id !== DESAGRUPADO_ITEM_ID);
    const merged = [...new Set([...realSelectedAssets, ...filteredIds])];
    setSelectedVehicles(merged);

    const desagrupadoActive =
      safeSelectedGroups.includes(SEM_GRUPO_GROUP_ID) || safeSelectedAssets.includes(DESAGRUPADO_ITEM_ID);
    setIncludeNullVehicle(desagrupadoActive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssets, selectedGroups]);

  return (
    <Tree
      items={items}
      groups={newTags}
      virtualizeThreshold={1000}
      expandedGroups={expandedGroups}
      hasMultiselect={true}
      selectedItems={selectedAssets}
      selectedGroups={selectedGroups}
      onSelectionChange={onSelectionChange}
      onExpandGroupsChange={(newExpandedGroup) => setExpandedGroups(newExpandedGroup)}
      searchPlaceholder={t("assetTree.vehicle.search")}
      showEmptyGroups={false}
    />
  );
};

export default VehicleTree;
